import { LoadingPage } from "@components/LoadingPage";
import { Redirect } from "@components/Redirect";
import { useAllowCreateFundraiser } from "@components/donate/DonateV3/useGridCardsDisplayController";
import { UUID } from "io-ts-types";
import { NextPage } from "next";
import { useRouter } from "next/router";
import React, { useContext, useEffect, useState } from "react";

import { SPECIAL_FUNDRAISER_NONPROFIT_SLUG } from "@every.org/common/src/entity/constants";
import {
  AdminLevel,
  NonprofitAdminStatus,
  atLeastAsPrivilegedAs,
} from "@every.org/common/src/entity/types";
import {
  getRoutePath,
  URLFormat,
  ClientRouteName,
} from "@every.org/common/src/helpers/clientRoutes";

import { DefaultPageLayout } from "src/components/layout/DefaultPageLayout";
import { PageSection } from "src/components/layout/PageSection";
import { AuthContext } from "src/context/AuthContext";
import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import { AuthStatus } from "src/context/AuthContext/types";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { useNonprofitAdminData } from "src/hooks/useNonprofitAdminData";
import ArchivedNonprofitPage from "src/pages/ArchivedNonprofit";
import {
  HeaderCardPageSection,
  MultiNonprofitFundraiserHeaderCardPageSection,
} from "src/pages/Fundraiser/CreatePage/HeaderCardPageSection";
import { Headline } from "src/pages/Fundraiser/CreatePage/styles";
import { FundraiserEditForm } from "src/pages/Fundraiser/EditForm";
import { FundraiserEditFormKind } from "src/pages/Fundraiser/EditForm/types";
import { SignupModal } from "src/pages/Fundraiser/SignupModal";
import { spacing, verticalStackCss } from "src/theme/spacing";
import { createGetServerSideProps } from "src/utilities/ssr/createGetServerSideProps";
import { getNonprofitOrUserServerSideProps } from "src/utilities/ssr/getNonprofitOrUserServerSideProps";
import { withPropsDeserialization } from "src/utilities/ssr/withPropsDeserialization";
import { getNonprofitPageServerSideProps } from "src/utility/apiClient/nonprofitPage";

interface FundraiserCreatePageProps {
  nonprofit: ContextNonprofit;
  forNonprofits: UUID[];
}

const FundraiserCreatePage: NextPage<FundraiserCreatePageProps> = ({
  nonprofit,
  forNonprofits: _forNonprofits,
}) => {
  const [forNonprofits, setForNonprofits] = useState<string[]>(_forNonprofits);

  const router = useRouter();

  useEffect(() => {
    const queryParams = { ...router.query };

    if (forNonprofits.length > 0) {
      queryParams.forNonprofits = forNonprofits.join(",");
    } else {
      delete queryParams.forNonprofits;
    }

    // Replace current URL without reloading the page
    router.replace(
      {
        pathname: router.pathname,
        query: queryParams,
      },
      undefined,
      { shallow: true }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forNonprofits]);
  const authState = useContext(AuthContext);

  if (nonprofit.archived) {
    return <ArchivedNonprofitPage nonprofit={nonprofit} />;
  }

  const isMultiNonprofitFundraiser =
    nonprofit.primarySlug === SPECIAL_FUNDRAISER_NONPROFIT_SLUG;

  const pageTitle = isMultiNonprofitFundraiser
    ? "Create a multi-nonprofit fundraiser"
    : `Create a fundraiser for ${nonprofit.name}`;

  const signupModalTitle = isMultiNonprofitFundraiser
    ? "Sign up to create a multi-nonprofit fundraiser"
    : `Sign up to fundraise for ${nonprofit.name}`;

  return (
    <RedirectHandler nonprofit={nonprofit}>
      <DefaultPageLayout
        pageTitle={pageTitle}
        headerContent={<Headline>{pageTitle}</Headline>}
        pageContentCss={[{ paddingTop: spacing.l }, verticalStackCss.xl]}
      >
        {authState.status === AuthStatus.LOGGED_OUT && (
          <SignupModal
            title={signupModalTitle}
            isModalOpen
            redirectUrl={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.FUNDRAISER_CREATE,
              tokens: {
                nonprofitSlug: nonprofit.primarySlug,
              },
            })}
          />
        )}
        {isMultiNonprofitFundraiser ? (
          <MultiNonprofitFundraiserHeaderCardPageSection
            forNonprofits={forNonprofits}
            onEdit={setForNonprofits}
          />
        ) : (
          <HeaderCardPageSection nonprofit={nonprofit} />
        )}
        <PageSection>
          <FundraiserEditForm
            nonprofit={nonprofit}
            forNonprofits={forNonprofits}
            kind={FundraiserEditFormKind.CREATE}
          />
        </PageSection>
      </DefaultPageLayout>
    </RedirectHandler>
  );
};

const RedirectHandler: React.FCC<{ nonprofit: ContextNonprofit }> = ({
  nonprofit,
  children,
}) => {
  const authState = useContext(AuthContext);
  const isAllowed = useAllowCreateFundraiser(nonprofit);
  const nonprofitAdmin = useNonprofitAdminData(nonprofit.id);
  const loggedInUser = useLoggedInUserOrUndefined();
  const isSuperAdmin = atLeastAsPrivilegedAs({
    level: loggedInUser?.adminLevelForDisplay,
    compareAgainst: AdminLevel.SUPER_ADMIN,
  });
  if (
    isAllowed ||
    isSuperAdmin ||
    nonprofitAdmin?.status === NonprofitAdminStatus.CONFIRMED
  ) {
    return children;
  }
  if (authState.status === AuthStatus.LOADING) {
    // So that potential nonprofit and super admins do not immediately redirect
    return <LoadingPage />;
  }
  return (
    <Redirect
      to={getRoutePath({
        format: URLFormat.RELATIVE,
        name: ClientRouteName.NONPROFIT_OR_CAUSE,
        tokens: { nonprofitSlug: nonprofit.primarySlug },
      })}
    />
  );
};

export const getServerSideProps = createGetServerSideProps(
  getNonprofitOrUserServerSideProps(getNonprofitPageServerSideProps)
);

export default withPropsDeserialization(FundraiserCreatePage);
